import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { useProjects } from '../context/ProjectContext';
import { usePermission } from '../auth/usePermission';
import { bugsApi } from '../api/acceptanceApi';
import { phaseApi } from '../api/endpoints';
import { getApiErrorMessage } from '../api/client';
import { notify } from '../components/Notifier';
import { formatDate } from '../utils/date';
import { BUG_STATUS_META, BUG_STATUS_OPTIONS } from './Bugs/statusMeta';
import { BUG_SEVERITY_META } from './Bugs/severityMeta';
import { BUG_SOURCE_COLOR, BUG_SOURCE_OPTIONS, bugSourceLabel } from './Bugs/sourceMeta';
import { BugDetailDialog } from './Bugs/BugDetailDialog';
import { AddBugDialog } from './Bugs/AddBugDialog';
import type { Bug, BugSource, BugStatus, Phase } from '../types';

/** 'OPEN_ALL' = bug còn mở (Mới + Đang sửa) — tức các tính năng đang Fixing. */
type StatusFilter = 'ALL' | 'OPEN_ALL' | BugStatus;
type SourceFilter = 'ALL' | BugSource;

/**
 * Trang danh sách lỗi (route /bugs). Khách hàng chỉ xem; thêm bug thực hiện
 * tại màn chi tiết tính năng (cần biết bug thuộc tính năng nào).
 */
export function Bugs(): JSX.Element {
  const { projects, selectedProjectId, setSelectedProjectId } = useProjects();
  const canCreate = usePermission('BUG_CREATE');
  const [addOpen, setAddOpen] = useState(false);

  const [phases, setPhases] = useState<Phase[]>([]);
  const [phaseFilter, setPhaseFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN_ALL');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('ALL');
  const [search, setSearch] = useState('');

  const [items, setItems] = useState<Bug[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Bug | null>(null);

  const navigate = useNavigate();
  const { id: ticketParam } = useParams<{ id?: string }>();

  useEffect(() => {
    setPage(0);
  }, [selectedProjectId, phaseFilter, statusFilter, sourceFilter]);

  useEffect(() => {
    setPhaseFilter('ALL');
    if (!selectedProjectId) {
      setPhases([]);
      return;
    }
    let active = true;
    phaseApi
      .listByProject(selectedProjectId, { limit: 200 })
      .then((result) => {
        if (active) setPhases(result.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [selectedProjectId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await bugsApi.list({
        project_id: selectedProjectId ?? undefined,
        phase_id: phaseFilter !== 'ALL' ? phaseFilter : undefined,
        status: statusFilter !== 'ALL' && statusFilter !== 'OPEN_ALL' ? statusFilter : undefined,
        open: statusFilter === 'OPEN_ALL' ? 1 : undefined,
        source: sourceFilter !== 'ALL' ? sourceFilter : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể tải danh sách lỗi.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, phaseFilter, statusFilter, sourceFilter, page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!ticketParam) {
      setSelected(null);
      return;
    }
    let active = true;
    bugsApi
      .get(ticketParam)
      .then((bug) => {
        if (!active) return;
        setSelected(bug);
        // Đồng bộ dự án đang chọn (ô Project trên AppBar) theo bug vừa mở qua
        // deep-link — tránh cảnh dialog hiện đúng bug nhưng danh sách phía sau
        // vẫn lọc theo dự án khác (hoặc rỗng) do chưa khớp project_id.
        if (bug.project_id !== selectedProjectId) {
          setSelectedProjectId(bug.project_id);
        }
      })
      .catch((err) => {
        if (active) {
          notify(getApiErrorMessage(err, 'Không tìm thấy lỗi.'), 'error');
          navigate('/bugs', { replace: true });
        }
      });
    return () => {
      active = false;
    };
    // selectedProjectId cố ý không nằm trong dependency — chỉ đồng bộ MỘT LẦN
    // khi mở theo ticketParam, không chạy lại mỗi khi người dùng tự đổi ô chọn
    // dự án trong lúc dialog đang mở.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketParam, navigate, setSelectedProjectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (bug) =>
        bug.title.toLowerCase().includes(q) ||
        (bug.description ?? '').toLowerCase().includes(q) ||
        (bug.feature_name ?? '').toLowerCase().includes(q) ||
        (bug.ticket_code ?? '').toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Lỗi (bug)
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Thêm lỗi
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="bug-project-label">Dự án</InputLabel>
              <Select
                labelId="bug-project-label"
                label="Dự án"
                value={selectedProjectId ?? ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="bug-phase-label">Giai đoạn</InputLabel>
              <Select
                labelId="bug-phase-label"
                label="Giai đoạn"
                value={phaseFilter}
                onChange={(e) => setPhaseFilter(e.target.value)}
              >
                <MenuItem value="ALL">Tất cả giai đoạn</MenuItem>
                {phases.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="bug-status-label">Trạng thái</InputLabel>
              <Select
                labelId="bug-status-label"
                label="Trạng thái"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="ALL">Tất cả trạng thái</MenuItem>
                <MenuItem value="OPEN_ALL">Đang mở (Mới + Đang sửa)</MenuItem>
                {BUG_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {BUG_STATUS_META[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="bug-source-label">Nguồn phát hiện</InputLabel>
              <Select
                labelId="bug-source-label"
                label="Nguồn phát hiện"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              >
                <MenuItem value="ALL">Tất cả nguồn</MenuItem>
                {BUG_SOURCE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {bugSourceLabel(s, projects.find((p) => p.id === selectedProjectId)?.customer_name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Tìm theo mã, tiêu đề, tính năng…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mã</TableCell>
                <TableCell>Tính năng</TableCell>
                <TableCell>Tiêu đề</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Mức độ</TableCell>
                <TableCell>Nguồn</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Ngày phát hiện</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Không có lỗi nào.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((bug) => {
                  const meta = BUG_STATUS_META[bug.status];
                  return (
                    <TableRow
                      key={bug.id}
                      hover
                      onClick={() => navigate(`/bugs/${bug.ticket_code ?? bug.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {bug.ticket_code ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{bug.feature_name ?? '—'}</Typography>
                        {bug.phase_name && (
                          <Typography variant="caption" color="text.secondary">
                            {bug.phase_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {bug.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={meta.label}
                          color={meta.color === 'default' ? 'default' : meta.color}
                          size="small"
                          variant={meta.color === 'default' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        {bug.severity ? (
                          <Chip
                            label={BUG_SEVERITY_META[bug.severity].label}
                            color={
                              BUG_SEVERITY_META[bug.severity].color === 'default'
                                ? 'default'
                                : BUG_SEVERITY_META[bug.severity].color
                            }
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bugSourceLabel(
                            bug.source,
                            projects.find((p) => p.id === bug.project_id)?.customer_name,
                          )}
                          color={BUG_SOURCE_COLOR[bug.source] === 'default' ? 'default' : BUG_SOURCE_COLOR[bug.source]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {bug.created_by_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(bug.discovered_at ?? bug.created_at)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      {selected && (
        <BugDetailDialog bug={selected} onClose={() => navigate('/bugs')} onChanged={() => void load()} />
      )}

      <AddBugDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false);
          void load();
        }}
      />
    </Box>
  );
}
